import React, { Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { LabelValue } from '@warehouse/shared/ui';
import { MediaInfoTrackMapping } from '@warehouse/object-browser/core';
import { mediaInfoTrackMappingRepositorySingleton } from '../../infra/mediaInfoTrackMapping.repository.json';

interface MediaInfoTrack {
	'@type': string;
	[key: string]: string | undefined;
}

interface MediaInfoData {
	media?: {
		track?: MediaInfoTrack[];
	};
}

interface MediaInfoDetailedProps {
	mediaInfo: string;
}

interface TrackGridProps {
	type: string;
	track: MediaInfoTrack;
	fields: MediaInfoTrackMapping[];
}

interface TrackSectionProps {
	type: string;
	tracks: MediaInfoTrack[];
	displayFieldsByType: Record<string, MediaInfoTrackMapping[]>;
}

function buildDisplayFieldsByType(): Record<string, MediaInfoTrackMapping[]> {
	const repo = mediaInfoTrackMappingRepositorySingleton.get();
	return repo.getMediaInfoTrackMappingConfig();
};

function streamsSummaryText(tracks: MediaInfoTrack[]): string {
	const counts: Record<string, number> = {};
	tracks.forEach((t) => {
		const type = t['@type'];
		if (type === 'General') return;
		counts[type] = (counts[type] ?? 0) + 1;
	});
	return Object.entries(counts)
		.map(([type, n]) => `${n} ${type.toLowerCase()}`)
		.join(', ');
};

function totalNonGeneralCount(tracks: MediaInfoTrack[]): number {
	return tracks.filter((t) => t['@type'] !== 'General').length;
};

function groupTracksByType(tracks: MediaInfoTrack[]): Record<string, MediaInfoTrack[]> {
	const groups: Record<string, MediaInfoTrack[]> = {};
	tracks.forEach((t) => {
		const type = t['@type'];
		if (!groups[type]) groups[type] = [];
		groups[type].push(t);
	});
	const track: Record<string, MediaInfoTrack[]> = {};
	Object.keys(groups).forEach((k) => {
		if (!track[k]) track[k] = groups[k];
	});
	return track;
};

function getTrackSummaryLine(
	track: MediaInfoTrack,
	type: string,
	index: number,
	fields: MediaInfoTrackMapping[],
): string {
	const num = `#${index + 1}`;
	const parts = fields
		.filter((f) => f.summary)
		.map((f) => track[`${f.key}_String`] ?? track[f.key])
		.filter(Boolean);
	return [`${type} ${num}`, ...parts].join(' · ');
};

function TrackGrid({ track, fields, type }: TrackGridProps) {
	const visibleFields = fields.filter((f) => track[f.key]);
	return (
		<Grid>
			{visibleFields.map((f) => 
				<FieldItem key={f.key} aria-label={`${f.label}-${type}`}>
					<LabelValue label={f.label} value={track[`${f.key}_String`] ?? track[f.key]} tooltip={f.hint} />							
				</FieldItem>
			)}
		</Grid>
	);
};

function TrackSection({ type, tracks, displayFieldsByType }: TrackSectionProps) {
	const [expandedSet, setExpandedSet] = useState<Set<number>>(new Set());
	const allExpanded = expandedSet.size === tracks.length;
	const fields = displayFieldsByType[type] ?? [];
	const countLabel = tracks.length === 1 ? '1 track' : `${tracks.length} tracks`;

	const toggleAll = () => {
		setExpandedSet(allExpanded ? new Set() : new Set(tracks.map((_, i) => i)));
	};

	const toggleItem = (index: number) => {
		setExpandedSet((prev) => {
			const next = new Set(prev);
			if (next.has(index)) next.delete(index);
			else next.add(index);
			return next;
		});
	};

	return (
		<div>
			<SectionHeaderRow>
				<div>
					<SectionTitle aria-label={`Track-Title-${type}`}>{type}</SectionTitle>
					<SectionCount aria-label={`Track-Count-${type}`}>{countLabel}</SectionCount>
				</div>
				<ExpandAllBtn onClick={toggleAll} aria-label={`ToggleAll-Button-${type}`}>
					{allExpanded ? 'Collapse all' : 'Expand all'}
					<ChevronRightIcon
						fontSize="small"
						style={{ transform: allExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}
					/>
				</ExpandAllBtn>
			</SectionHeaderRow>
			<SectionDivider />
			{tracks.map((track, index) => {
				const expanded = expandedSet.has(index);
				return (
					<Fragment key={index}>
						<TrackRow onClick={() => toggleItem(index)}>
							<TrackLabel aria-label={`Track-Summary-Line-${type}-${index}`}>
								{getTrackSummaryLine(track, type, index, fields)}
							</TrackLabel>
							<ChevronRightIcon
								fontSize="small"
								style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}
							/>
						</TrackRow>
						{expanded && (
							<TrackDetail aria-label={`Track-${type}-${index}`}>
								<TrackGrid track={track} fields={fields} type={type} />
							</TrackDetail>
						)}
					</Fragment>
				);
			})}
		</div>
	);
};

export function MediaInfoDetailed({ mediaInfo }: MediaInfoDetailedProps) {
	const parsed = useMemo<MediaInfoData | null>(() => {
		try {
			return JSON.parse(mediaInfo);
		} catch {
			return null;
		}
	}, [mediaInfo]);

	const tracks = useMemo<MediaInfoTrack[]>(() => parsed?.media?.track ?? [], [parsed]);
	const groups = useMemo(() => groupTracksByType(tracks), [tracks]);
	const displayFieldsByType = useMemo(() => buildDisplayFieldsByType(), []);

	const general = groups.General?.[0];
	let containerValue: string | undefined;
	if (general) {
		containerValue = general.Format_Profile
			? `${general.Format_String} (${general.Format_Profile})`
			: general.Format_String;
	}
	const nonGeneralTypes = Object.keys(groups).filter((t) => t !== 'General');

	return (
		<PaddingWrapper>
			<Container id="MediaInfo-Detailed-Container">
				<Grid>
					<FieldItem aria-label="MediaInfo-Container">
						<LabelValue label="Container" value={containerValue ?? '--'} />
					</FieldItem>
					<FieldItem aria-label="MediaInfo-Duration">
						<LabelValue label="Duration" value={general?.Duration_String ?? '--'} />
					</FieldItem>
					<FieldItem aria-label="MediaInfo-Overall">
						<LabelValue label="Overall Bit Rate" value={general?.OverallBitRate_String ?? '--'} />
					</FieldItem>
					<FieldItem aria-label="MediaInfo-FileSize">
						<LabelValue label="File Size" value={general?.FileSize_String ?? '--'} />
					</FieldItem>

					{tracks.length > 0 && (
						<FieldItem aria-label="MediaInfo-Streams">
							<LabelValue label="Streams" value={streamsSummaryText(tracks)} />
						</FieldItem>
					)}
				</Grid>
			
				{general && (
					<div>
						<SectionHeaderRow>
							<div>
								<SectionTitle aria-label="General-Track">General</SectionTitle>
								<SectionCount aria-label="StreamsCount">
									{totalNonGeneralCount(tracks) === 1 ? '1 stream' : `${totalNonGeneralCount(tracks)} streams`}
								</SectionCount>
							</div>
						</SectionHeaderRow>
						<SectionDivider />
						<TrackGrid track={general} fields={displayFieldsByType.General ?? []} type="General" />
					</div>
				)}

				{nonGeneralTypes.map((type) => (
					<TrackSection key={type} type={type} tracks={groups[type]} displayFieldsByType={displayFieldsByType} />
				))}
			</Container>
		</PaddingWrapper>
	);
};

const PaddingWrapper = styled('div')(({ theme }) => ({
    flex: 1,
    minHeight: 0,  
    display: 'flex',        
    flexDirection: 'column',
	gridRowGap: theme.spacing(2),
	gridTemplateRows: '34% 30% 18% 18%',
    padding: theme.spacing(6, 1),
    boxSizing: 'border-box',
    overflow: 'hidden',     
}));

const Container = styled('div')({
    flex: 1,                
    minHeight: 0,           
    display: 'flex',
    flexDirection: 'column',
	overflowX: 'hidden',
    overflowY: 'auto',      
    paddingBottom: 16,
});

const Grid = styled('div')(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: 'repeat(4, 1fr)',
	gap: theme.spacing(3),
	paddingLeft: theme.spacing(1.5),
}));

const FieldItem = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
  overflow: 'visible',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
});

const SectionDivider = styled('hr')({
  border: 'none',
  borderTop: '1px solid #e5e7eb',
  margin: '6px 0 20px 20px',
});


const SectionHeaderRow = styled('div')({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  paddingTop: 20,
});

const SectionTitle = styled('span')({
  color: '#111827',
  fontSize: 15,
  fontWeight: 700,
  marginRight: 8,
  marginLeft: 20,
});

const SectionCount = styled('span')({
  color: '#9ca3af',
  fontSize: 13,
});

const ExpandAllBtn = styled('button')({
  alignItems: 'center',
  background: 'none',
  border: 'none',
  color: '#6b7280',
  cursor: 'pointer',
  display: 'flex',
  fontSize: 13,
  gap: 2,
  padding: 0,
  '&:hover': {
    color: '#111827',
  },
});

const TrackRow = styled('div')({
  alignItems: 'center',
  borderBottom: '1px solid #f3f4f6',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '10px 0',
  marginLeft: 20,
  '&:hover': {
    background: '#f9fafb',
  },
});

const TrackLabel = styled('span')({
  color: '#374151',
  fontSize: 13,
});

const TrackDetail = styled('div')({
  padding: '16px 0 8px',
});